import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import {
  Leaf, Cloud, Wind, Sun, Tent, Copy, Check, ArrowLeft, MapPin, Sparkles, Flame,
} from "lucide-react";
import { withUtm } from "../lib/utm";

/**
 * /share/autumn-camping — 4-scene 9:16 reel driving autumn sign-ups.
 *
 * Angle: crisp, dry, uncrowded — the sweet spot the UK forgets exists.
 * Voice: warm, no marketing-speak, one-tap CTA to the app.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const APP_URL = withUtm(PUBLIC_URL, { source: "autumn", medium: "reel", campaign: "autumn-camping-2026" });
const BROWSE_URL = withUtm(`${PUBLIC_URL}/browse`, { source: "autumn", medium: "reel", campaign: "autumn-camping-2026-browse" });

const CAPTION_LONG = `Autumn is quietly the best UK camping season 🍂

Empty fields. Crisp mornings. Zero midges. The dry stretch we always forget shows up in September–October just before the clocks go back.

If you've been meaning to try somewhere new — this is the window.

→ 22,000+ UK campsites on Camp Compass
→ Filter by dogs, campfires, family, adults-only
→ Real trip stories from other campers (not marketing fluff)
→ Free to browse · £1.99/mo Pro after a 14-day trial

Tap the link. Pick a patch. Go before the weather turns.

camp-compass.uk

#AutumnCamping #UKCamping #StaycationUK #Wildcamping #CampCompass`;

const CAPTION_SHORT = `Autumn = the UK's best-kept camping secret 🍂
Empty fields. Dry weather. Zero midges.
camp-compass.uk`;

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden shadow-[0_4px_0_0_#0e0e0e]"
        style={{ aspectRatio: "9 / 16", maxWidth: 380, background: bg }}
        data-testid={testid}
      >
        {children}
      </section>
    </div>
  );
}

function Sticker({ children, color = "#ff4500", textColor = "#fff" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em]"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function CaptionBox({ label, text }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border hairline bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{label}</div>
        <button
          onClick={() => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}
          className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#ff4500] hover:text-[#0e0e0e]"
          data-testid={`autumn-caption-copy-${label.split(" ")[0].toLowerCase()}`}
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-xs text-[#333] leading-relaxed font-sans">{text}</pre>
    </div>
  );
}

export default function AutumnCampingReel() {
  return (
    <main className="min-h-screen bg-[#fafafa] px-4 sm:px-8 py-8 sm:py-12" data-testid="reel-autumn-camping">
      <div className="max-w-5xl mx-auto">
        <Link to="/launch-pack" className="inline-flex items-center gap-1.5 text-xs mono uppercase tracking-wider text-[#0e0e0e] hover:text-[#ff4500] mb-6">
          <ArrowLeft className="w-3 h-3" /> Back to Launch Pack
        </Link>
        <header className="mb-8">
          <div className="eyebrow text-[#ff4500] mb-2">Reel · 9:16 · 4 scenes</div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight mb-2">Autumn camping</h1>
          <p className="text-sm text-[#4a4a4a] max-w-2xl">Push sign-ups in the September–October dry stretch. Empty fields, crisp mornings, zero midges — the UK&rsquo;s quietly-best camping season.</p>
        </header>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {/* SCENE 01 — THE HOOK */}
          <Scene bg="linear-gradient(180deg,#d97706 0%,#7c2d12 100%)" testid="autumn-scene-01" label="01 · Hook">
            <div className="p-5 flex flex-col h-full justify-between">
              <div>
                <Sticker><Leaf className="w-3 h-3" strokeWidth={2.5} />Autumn is here</Sticker>
              </div>
              <div>
                <div className="text-5xl sm:text-6xl font-black leading-[0.95] mb-4 tracking-tight drop-shadow-md">The UK&rsquo;s<br />best-kept<br />camping<br />secret.</div>
                <div className="text-sm mono uppercase tracking-widest text-white/85">→ Swipe up</div>
              </div>
            </div>
          </Scene>

          {/* SCENE 02 — WHY NOW */}
          <Scene bg="linear-gradient(180deg,#059669 0%,#052e16 100%)" testid="autumn-scene-02" label="02 · Why now">
            <div className="p-5 flex flex-col h-full justify-between">
              <div>
                <Sticker color="#fef3c7" textColor="#7c2d12"><Sun className="w-3 h-3" strokeWidth={2.5} />4 reasons</Sticker>
              </div>
              <div className="space-y-3">
                {[
                  { icon: Sun, label: "Dry, crisp, cool" },
                  { icon: Wind, label: "Zero midges" },
                  { icon: Cloud, label: "Empty fields" },
                  { icon: Tent, label: "Half the price" },
                ].map(({ icon: Icon, label }, i) => (
                  <div key={i} className="flex items-center gap-3 bg-black/25 backdrop-blur px-3 py-2.5">
                    <Icon className="w-5 h-5 text-[#facc15] flex-shrink-0" strokeWidth={2} />
                    <span className="text-base font-bold text-white leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Scene>

          {/* SCENE 03 — THE APP */}
          <Scene bg="linear-gradient(180deg,#0e0e0e 0%,#ff4500 100%)" testid="autumn-scene-03" label="03 · The app">
            <div className="p-5 flex flex-col h-full justify-between">
              <Sticker><MapPin className="w-3 h-3" strokeWidth={2.5} />Camp Compass</Sticker>
              <div>
                <div className="text-4xl sm:text-5xl font-black leading-tight mb-3 drop-shadow-md">22,000+<br />UK sites.<br />One tap.</div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#facc15]" /> Dogs · campfires · family filters</li>
                  <li className="flex items-center gap-2"><Flame className="w-4 h-4 text-[#facc15]" /> Real trip stories · no marketing fluff</li>
                  <li className="flex items-center gap-2"><Tent className="w-4 h-4 text-[#facc15]" /> Free · Pro from £1.99/mo</li>
                </ul>
              </div>
              <div className="text-sm mono uppercase tracking-widest text-white/85">camp-compass.uk</div>
            </div>
          </Scene>

          {/* SCENE 04 — CTA */}
          <Scene bg="linear-gradient(180deg,#7c2d12 0%,#facc15 100%)" testid="autumn-scene-04" label="04 · Go">
            <div className="p-5 flex flex-col h-full justify-between items-center text-center">
              <Sticker color="#0e0e0e" textColor="#facc15">Go before the rain</Sticker>
              <div className="flex flex-col items-center gap-3">
                <div className="bg-white p-3 shadow-[0_4px_0_0_#0e0e0e]">
                  <QRCodeSVG value={APP_URL} size={140} includeMargin={false} level="M" />
                </div>
                <div className="text-white text-xs mono uppercase tracking-widest">Scan · pick · pitch</div>
              </div>
              <div>
                <div className="text-3xl font-black leading-tight drop-shadow-md">Pick a patch.<br />Go this weekend.</div>
                <div className="text-xs mono uppercase tracking-widest mt-3 text-white/85">camp-compass.uk</div>
              </div>
            </div>
          </Scene>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <CaptionBox label="Long caption" text={CAPTION_LONG} />
          <CaptionBox label="Short caption" text={CAPTION_SHORT} />
        </div>

        <div className="text-xs mono text-[#666] flex flex-wrap gap-4">
          <Link to={BROWSE_URL} className="underline hover:text-[#ff4500]">Preview Browse →</Link>
          <Link to="/launch-pack" className="underline hover:text-[#ff4500]">Back to Launch Pack</Link>
        </div>
      </div>
    </main>
  );
}
